import { useEdpAiSubmissionDelete } from '@monorepo/shared/hooks/logs/useEdpAiSubmissionDelete';
import { ErrorIcon } from '@monorepo/shared/componentsV2/errorDisplays/ErrorIcon';
import { useToast } from '@monorepo/shared/contexts';
import { useModal } from '@monorepo/shared/hooks/useModalV2';
import { EdpAiSubmissionRefResponse } from 'mapistry-shared';
import React, { useCallback } from 'react';
import styled from 'styled-components';

const TitleWithIcon = styled.span`
  display: inline-flex;
  gap: 0.5rem;
  align-items: center;
`;

function getDeleteEntriesLabel(entryCount: number) {
  return entryCount === 1 ? 'Delete 1 entry' : `Delete ${entryCount} entries`;
}

function getDeleteEntriesDescription(entryCount: number) {
  const entryNoun = entryCount === 1 ? 'Log entry' : 'Log entries';
  return `Deleting this file will delete ${entryCount} associated ${entryNoun}. Are you sure you want to continue?`;
}

function getDeleteSuccessMessage(entryCount: number) {
  const entryNoun = entryCount === 1 ? 'Log entry has' : 'Log entries have';
  return `${entryCount} associated ${entryNoun} been deleted.`;
}

type UseConfirmDeleteEdpAiSubmissionProps = {
  logId: string;
  organizationId: string;
  projectId: string;
};

export function useConfirmDeleteEdpAiSubmission({
  logId,
  organizationId,
  projectId,
}: UseConfirmDeleteEdpAiSubmissionProps) {
  const [deleter] = useEdpAiSubmissionDelete({
    config: { throwOnError: true },
  });
  const { confirm } = useModal();
  const { success, showUserFriendlyErrorToast } = useToast();

  const confirmDeleteEdpAiSubmission = useCallback(
    async (submission: EdpAiSubmissionRefResponse) => {
      const { entryCount, id: edpAiSubmissionId } = submission;
      const deleteLabel = getDeleteEntriesLabel(entryCount);

      const handleDeleteConfirm = async (isConfirmed: boolean) => {
        if (isConfirmed) {
          await deleter({
            edpAiSubmissionId,
            logId,
            organizationId,
            projectId,
          });
        }
      };

      try {
        const isConfirmed = await confirm({
          title: (
            <TitleWithIcon>
              <ErrorIcon aria-hidden />
              {deleteLabel}
            </TitleWithIcon>
          ),
          description: getDeleteEntriesDescription(entryCount),
          cancelButtonText: 'Cancel',
          confirmButtonText: deleteLabel,
          afterConfirmButtonText: 'Deleting...',
          danger: true,
          onConfirmAsync: handleDeleteConfirm,
        });
        if (isConfirmed) {
          success(getDeleteSuccessMessage(entryCount));
        }
      } catch (err) {
        showUserFriendlyErrorToast(err, 'Unable to delete entries.', {
          dontAutoHide: true,
        });
      }
    },
    [
      confirm,
      deleter,
      logId,
      organizationId,
      projectId,
      showUserFriendlyErrorToast,
      success,
    ],
  );

  return { confirmDeleteEdpAiSubmission };
}
